import type { CoastalSceneState } from './coastal-types';
import { surfCrabCycleAt } from './window-moments';
import { clampSurf, easeSurf, surfCycleSeconds, surfIrregularity, surfShorePoint } from './surf-water';

function seedOffset(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (Math.imul(hash, 31) + seed.charCodeAt(i)) >>> 0;
  return hash;
}

/** Rare, deterministic gestures. They never change the selected conditions. */
export function surfMomentsAt(state: CoastalSceneState, elapsed: number) {
  const time = elapsed / surfCycleSeconds(state);
  const cycle = Math.floor(time), phase = time - cycle;
  const offset = seedOffset(state.seed);
  const roughness = surfIrregularity(state);
  const event = (cycle + offset) % 5;
  // Messier seas have more failed attempts, with at least one recovery cycle
  // between them. These are illustrative antics, never predicted accidents.
  const wipeout = state.combinedWaveHeightM >= 0.55 && roughness >= 0.4
    && (event === 0 || (roughness >= 0.65 && event === 3));
  const sting = !wipeout && (cycle + offset) % 7 === 2
    && (state.reports?.some((report) => report.kind === 'jellyfish') ?? false);
  return {
    wipeout, sting,
    celebrate: !sting && !wipeout && (cycle + offset) % 4 === 0 && state.cleanliness01 >= 0.78
      && state.combinedWaveHeightM >= 0.55 && state.combinedWaveHeightM <= 2.5,
    look: (cycle + offset) % 5 === 2 && phase < 0.115,
    warm: (cycle + offset) % 3 === 1 && phase >= 0.2 && phase < 0.36
      && state.weather.temperatureC < 10,
    lookPose: clampSurf(phase / 0.115),
    warmPose: clampSurf((phase - 0.2) / 0.16),
  };
}

/** A small crab scuttles along the dry side of the beach, once in 80 seconds. */
export function surfCrabAt(state: CoastalSceneState, elapsed: number) {
  const occurrence = surfCrabCycleAt(state, elapsed);
  if (!occurrence) return null;
  const { cycle, phase } = occurrence;
  const t = easeSurf(phase);
  const along = cycle % 2 ? 0.88 - t * 0.08 : 0.8 + t * 0.08;
  const shore = surfShorePoint(along, state.tide.position01, state.coastGeometry);
  return { x: shore.x + 33, y: shore.y + 4, opacity: easeSurf(phase / 0.12) * easeSurf((1 - phase) / 0.12),
    step: Math.sin(elapsed * 17), direction: cycle % 2 ? 1 : -1 };
}
